import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink, ShoppingBag, Share2, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import Navbar from './Navbar';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('http')) return src;
  return `${BACKEND_URL}${src}`;
};

const GuideDetail = () => {
  const { slug } = useParams();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    axios
      .get(`${API}/guides/${slug}`)
      .then((r) => setGuide(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const paragraphs = (guide?.content || '').split(/\n\n+/).filter((p) => p.trim());

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = guide?.title ? `${guide.title} — DRYWALL TOOLS` : 'DRYWALL TOOLS';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url: shareUrl });
      } catch {
        /* usuario canceló */
      }
    } else {
      copyLink();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-24 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <Link
          to="/guias"
          data-testid="guide-back"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-red-600 transition-colors mb-4"
        >
          <ArrowLeft size={14} /> Todas las guías
        </Link>

        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-red-600 border-t-transparent mx-auto" />
          </div>
        ) : notFound || !guide ? (
          <p className="text-center text-slate-400 py-16">Guía no encontrada.</p>
        ) : (
          <article data-testid="guide-article">
            <span className="text-xs uppercase font-bold text-red-600">
              {guide.category || 'General'}
            </span>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 leading-tight">
              {guide.title}
            </h1>
            {guide.excerpt && (
              <p className="text-lg text-slate-600 mt-3">{guide.excerpt}</p>
            )}
            {guide.cover_image && (
              <img
                src={resolveImg(guide.cover_image)}
                alt={guide.title}
                className="w-full rounded-xl mt-6 object-cover max-h-96"
              />
            )}

            <div className="prose prose-slate max-w-none mt-8 space-y-4">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-slate-700 leading-relaxed whitespace-pre-line">
                  {p}
                </p>
              ))}
            </div>

            {/* Affiliate / recommended products */}
            {guide.products && guide.products.length > 0 && (
              <div className="mt-12" data-testid="guide-products">
                <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-2">
                  <ShoppingBag size={22} className="text-red-600" /> Herramientas recomendadas
                </h2>
                <p className="text-xs text-slate-400 mb-5">
                  Enlaces de afiliado: si compras a través de ellos podemos ganar una pequeña
                  comisión, sin coste extra para ti.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {guide.products.map((p, i) => (
                    <div
                      key={i}
                      data-testid={`guide-product-${i}`}
                      className="border border-slate-200 rounded-xl p-4 flex gap-4 hover:shadow-md transition-shadow"
                    >
                      <div
                        className="w-20 h-20 bg-slate-100 rounded-lg bg-cover bg-center flex-shrink-0"
                        style={{
                          backgroundImage: p.image ? `url(${resolveImg(p.image)})` : 'none',
                        }}
                      />
                      <div className="flex-1 min-w-0 flex flex-col">
                        <h3 className="font-semibold text-slate-900 text-sm leading-tight">
                          {p.name}
                        </h3>
                        {p.note && (
                          <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.note}</p>
                        )}
                        <div className="mt-auto flex items-center justify-between pt-2">
                          {p.price_text && (
                            <span className="text-sm font-bold text-red-600">{p.price_text}</span>
                          )}
                          <a
                            href={p.url || '#'}
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            data-testid={`guide-product-link-${i}`}
                          >
                            <Button
                              size="sm"
                              className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs"
                            >
                              Ver oferta <ExternalLink size={12} className="ml-1" />
                            </Button>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-12 bg-slate-50 rounded-xl p-6 text-center">
              <p className="text-slate-700 font-semibold">
                ¿Necesitas un diseño de planos o asesoría para tu proyecto?
              </p>
              <Link to="/#paquetes">
                <Button className="mt-3 bg-red-600 hover:bg-red-700 text-white">
                  Ver nuestros servicios
                </Button>
              </Link>
            </div>

            {/* Compartir */}
            <div className="mt-10 border-t border-slate-200 pt-6" data-testid="guide-share">
              <p className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
                <Share2 size={16} className="text-red-600" /> ¿Te ha sido útil? Compártela
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-whatsapp"
                >
                  <Button
                    size="sm"
                    className="bg-[#25D366] hover:bg-[#1ebe5b] text-white h-9 text-xs"
                  >
                    WhatsApp
                  </Button>
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-facebook"
                >
                  <Button
                    size="sm"
                    className="bg-[#1877F2] hover:bg-[#166fe0] text-white h-9 text-xs"
                  >
                    Facebook
                  </Button>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-twitter"
                >
                  <Button size="sm" className="bg-black hover:bg-slate-800 text-white h-9 text-xs">
                    X (Twitter)
                  </Button>
                </a>
                <Button
                  onClick={copyLink}
                  size="sm"
                  variant="outline"
                  data-testid="share-copy"
                  className="border-slate-300 text-slate-700 h-9 text-xs"
                >
                  {copied ? (
                    <>
                      <Check size={14} className="mr-1 text-green-600" /> Copiado
                    </>
                  ) : (
                    <>
                      <Link2 size={14} className="mr-1" /> Copiar enlace
                    </>
                  )}
                </Button>
                <Button
                  onClick={nativeShare}
                  size="sm"
                  variant="outline"
                  data-testid="share-native"
                  className="border-slate-300 text-slate-700 h-9 text-xs sm:hidden"
                >
                  <Share2 size={14} className="mr-1" /> Más
                </Button>
              </div>
            </div>
          </article>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default GuideDetail;
